import { useMemo } from "react"
import { differenceInCalendarDays, format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import Chart from "@/v2-ui/chart"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import { DotText } from "@/v2-backoffice-client/components/dot"
import {
  getPaymentHealthConfig,
  getValueTierConfig,
} from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import {
  deriveMonthlyBilledBuckets,
  formatInvoiceAmount,
  type MonthlyBilledBucket,
} from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { CustomerInvoiceFragment } from "@/v2-backoffice-client/customer/billing/__types__/customer.invoice.fragment"
import type { CustomerRevenueStatsFragment } from "@/v2-backoffice-client/customer/billing/__types__/customer.revenueStats.fragment"

const CHART_MONTHS = 24

// Brand data colors, not theme chrome (charts can't read CSS vars): green
// for money that came in, amber for money still expected, coral for money
// that never came.
const CHART_CONFIG = {
  collected: { label: "Collected", color: DATA_VIZ.brand.green2 },
  outstanding: { label: "Outstanding", color: DATA_VIZ.brand.amber },
  writtenOff: { label: "Written off", color: DATA_VIZ.semantic.negative },
}

export type CustomerBillingStatsProps = {
  stats: CustomerRevenueStatsFragment
  invoices: CustomerInvoiceFragment[]
}

// The Billing tab's revenue headline: three stat cards (value rank, payment
// health, lifetime billed-vs-collected) over a billed-per-month chart. The
// cards read the server-derived lic.CustomerRevenueFacts row — the same
// numbers the customer board's Value/Payment columns rank on — while the
// chart is derived client-side from the invoice list the tab already loads.
function CustomerBillingStats(props: CustomerBillingStatsProps) {
  const { stats, invoices } = props

  const currency = stats.PrimaryCurrency
  const tierConfig = getValueTierConfig(stats.ValueTier)
  const healthConfig = getPaymentHealthConfig(stats.PaymentHealth)

  // "top N %" from rank N of M — rank 3 of 87 reads "top 4 %".
  const topShare =
    stats.RevenueRank != null && stats.RankedCustomerCount
      ? Math.max(
          1,
          Math.round((stats.RevenueRank / stats.RankedCustomerCount) * 100),
        )
      : null

  // Momentum vs the 12 months before: only meaningful once both windows
  // have revenue.
  const billedDeltaPct =
    stats.Billed12M != null &&
    stats.BilledPrior12M != null &&
    stats.BilledPrior12M > 0
      ? Math.round(
          ((stats.Billed12M - stats.BilledPrior12M) / stats.BilledPrior12M) *
            100,
        )
      : null

  const collectionRatePct =
    stats.LifetimeBilled != null &&
    stats.LifetimeBilled > 0 &&
    stats.LifetimeCollected != null
      ? Math.round((stats.LifetimeCollected / stats.LifetimeBilled) * 100)
      : null

  const oldestOverdueDays = stats.OldestOverdueDueDate
    ? differenceInCalendarDays(new Date(), new Date(stats.OldestOverdueDueDate))
    : null

  const chartData = useMemo(
    () => deriveMonthlyBilledBuckets(invoices, CHART_MONTHS),
    [invoices],
  )
  const hasChartData = chartData.some(
    (bucket) =>
      bucket.collected > 0 || bucket.outstanding > 0 || bucket.writtenOff > 0,
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Customer value</h3>
          <div className="flex items-center gap-2">
            {tierConfig && (
              <DotText variant={tierConfig.variant} className="font-semibold">
                {tierConfig.label}
              </DotText>
            )}
            {stats.RevenueRank != null && stats.RankedCustomerCount != null && (
              <span className="text-xs text-muted-foreground">
                #{stats.RevenueRank} of {stats.RankedCustomerCount}
                {topShare != null && ` · top ${topShare} %`}
              </span>
            )}
          </div>
          <p className="text-sm tabular-nums">
            {formatInvoiceAmount(stats.Billed12M ?? 0, currency)}
            <span className="text-muted-foreground"> billed last 12 mo</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {billedDeltaPct != null
              ? `${billedDeltaPct >= 0 ? "+" : ""}${billedDeltaPct} % vs prior 12 mo`
              : `${formatInvoiceAmount(stats.BilledPrior12M ?? 0, currency)} the 12 mo before`}
          </p>
        </section>

        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Payment health</h3>
          {healthConfig && (
            <DotText variant={healthConfig.variant} className="font-semibold">
              {healthConfig.label}
            </DotText>
          )}
          <p className="text-sm tabular-nums">
            {formatInvoiceAmount(stats.OutstandingBalance ?? 0, currency)}
            <span className="text-muted-foreground"> outstanding</span>
          </p>
          {stats.OverdueBalance != null && stats.OverdueBalance > 0 ? (
            <p className="text-xs text-muted-foreground">
              {formatInvoiceAmount(stats.OverdueBalance, currency)} overdue
              {stats.OverdueInvoiceCount > 0 &&
                ` · ${stats.OverdueInvoiceCount} invoice${stats.OverdueInvoiceCount === 1 ? "" : "s"}`}
              {oldestOverdueDays != null &&
                oldestOverdueDays > 0 &&
                ` · oldest ${oldestOverdueDays} days`}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing overdue</p>
          )}
          {stats.NonpaymentBalance != null && stats.NonpaymentBalance > 0 && (
            <p className="text-xs text-muted-foreground">
              {formatInvoiceAmount(stats.NonpaymentBalance, currency)} written
              off / in collection
            </p>
          )}
        </section>

        <section className="flex flex-col gap-1 border-l-2 border-border pl-3">
          <h3 className="text-xs text-muted-foreground">Lifetime</h3>
          <p className="text-sm tabular-nums">
            {formatInvoiceAmount(stats.LifetimeBilled ?? 0, currency)}
            <span className="text-muted-foreground"> billed</span>
          </p>
          <p className="text-sm tabular-nums">
            {formatInvoiceAmount(stats.LifetimeCollected ?? 0, currency)}
            <span className="text-muted-foreground">
              {" "}
              collected
              {collectionRatePct != null && ` (${collectionRatePct} %)`}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            {stats.InvoiceCount} invoice{stats.InvoiceCount === 1 ? "" : "s"}
            {stats.FirstInvoiceDate &&
              ` since ${format(new Date(stats.FirstInvoiceDate), "MMM yyyy")}`}
          </p>
        </section>
      </div>

      {hasChartData && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold">Billed per month</h3>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              {Object.values(CHART_CONFIG).map((series) => (
                <span key={series.label} className="flex items-center gap-1">
                  <span
                    className="size-2 rounded-full"
                    style={{ backgroundColor: series.color }}
                  />
                  {series.label}
                </span>
              ))}
            </span>
          </div>

          <Chart config={CHART_CONFIG} className="h-[160px] w-full">
            <Chart.BarChart data={chartData} margin={{ left: -12, right: 4 }}>
              <Chart.Grid vertical={false} />
              <Chart.Axis.X
                dataKey="month"
                tickFormatter={(value: string) =>
                  format(new Date(`${value}-01`), "MMM yy")
                }
                tickLine={false}
                axisLine={false}
                tickMargin={6}
                minTickGap={28}
                className="text-xs text-muted-foreground"
              />
              <Chart.Axis.Y
                tickLine={false}
                axisLine={false}
                tickMargin={4}
                width={56}
                allowDecimals={false}
                className="text-xs text-muted-foreground"
              />
              <Chart.Tooltip
                content={<BilledMonthTooltip currency={currency} />}
              />
              <Chart.Bar
                dataKey="collected"
                stackId="billed"
                fill="var(--color-collected)"
                maxBarSize={18}
              />
              <Chart.Bar
                dataKey="outstanding"
                stackId="billed"
                fill="var(--color-outstanding)"
                maxBarSize={18}
              />
              <Chart.Bar
                dataKey="writtenOff"
                stackId="billed"
                fill="var(--color-writtenOff)"
                radius={[2, 2, 0, 0]}
                maxBarSize={18}
              />
            </Chart.BarChart>
          </Chart>
        </section>
      )}
    </div>
  )
}

// "What was billed that month and where the money stands" — total plus the
// collected / outstanding / written-off split for the hovered bar.
function BilledMonthTooltip(props: {
  active?: boolean
  payload?: { payload: MonthlyBilledBucket }[]
  currency?: string | null
}) {
  const { active, payload, currency } = props
  const bucket = payload?.[0]?.payload
  if (!active || !bucket) return null

  const total = bucket.collected + bucket.outstanding + bucket.writtenOff
  const parts = [
    { label: "Collected", value: bucket.collected },
    { label: "Outstanding", value: bucket.outstanding },
    { label: "Written off", value: bucket.writtenOff },
  ].filter((part) => part.value > 0)

  return (
    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      <p className="font-medium">
        {format(new Date(`${bucket.month}-01`), "MMMM yyyy")}
      </p>
      {total > 0 ? (
        <>
          <p className="text-muted-foreground">
            {formatInvoiceAmount(total, currency)} billed
          </p>
          {parts.length > 1 &&
            parts.map((part) => (
              <p key={part.label} className="text-muted-foreground">
                {part.label} {formatNumber(part.value)}
              </p>
            ))}
        </>
      ) : (
        <p className="text-muted-foreground">Nothing billed</p>
      )}
    </div>
  )
}

export default CustomerBillingStats
